import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { CORES_DO_RADAR, Radar10, type SerieDoRadar } from '@/components/conselheiro/Radar10.js';
import { PlanoDoCiclo, usePlano } from '@/components/conselheiro/PlanoDoCiclo.js';
import { Dica } from '@/components/Dica.js';
import { CicloMensal } from '@/components/conselheiro/CicloMensal.js';
import { Concorrentes } from '@/components/conselheiro/Concorrentes.js';
import { BalancoDoCiclo } from '@/components/conselheiro/BalancoDoCiclo.js';
import { LIMIAR_DE_DIVERGENCIA, TEMAS, TIPOS_DE_RADAR, motivoParaRecusarRadar } from '@shared/conselheiro.js';

/**
 * A empresa vista pelo conselheiro: o radar. O Principal abre o radar, os
 * sócios respondem o autodiagnóstico no app, e ele fecha com a nota de cada
 * tema e a evidência que a sustenta. A média dos sócios é o ponto de partida,
 * não a nota final.
 */

type Tipo = (typeof TIPOS_DE_RADAR)[number];
type Radar = {
  id: string;
  tipo: Tipo;
  data: string;
  aberto: boolean;
  fechado_em: string | null;
  ciclo_id: string | null;
  socios: {
    user: { id: string; name: string; email: string };
    respondidas: number;
    concluida: boolean;
    temas: number[] | null;
  }[];
  consolidado: { media: number[]; amplitude: number[]; divergentes: number[] };
  notas: number[] | null;
  evidencias: { tema: number; descricao: string; indicador: { id: string; nome: string } | null }[];
  observacoes: string | null;
};
type Dados = {
  empresa: { id: string; nome: string };
  meu_papel: string;
  ciclo_ativo: { id: string; inicio_em: string; fim_em: string | null; mes: number } | null;
  radares: Radar[];
};

const NOME: Record<Tipo, string> = { PARTIDA: 'Radar de partida', MARCO: 'Radar de marco', FINAL: 'Radar final' };
const data = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
const nota = (n: number) => n.toFixed(1).replace('.', ',');

function Cartao({ titulo, sub, children, acao }: { titulo: string; sub?: ReactNode; children: ReactNode; acao?: ReactNode }) {
  return (
    <section className="rounded-card border border-ha-border2 bg-ha-card p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-ha-text">{titulo}</h2>
          {sub ? <p className="mt-0.5 text-xs text-ha-muted">{sub}</p> : null}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

const botao =
  'rounded-element px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-50';

export function ConselheiroEmpresaPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const chave = ['conselheiro-radares', id];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Dados>(`/conselheiro/empresas/${id}/radares`)).data,
  });
  const [erro, setErro] = useState<string | null>(null);

  const abrir = useMutation({
    mutationFn: async (tipo: Tipo) => (await api.post(`/conselheiro/empresas/${id}/radares`, { tipo })).data,
    onSuccess: () => {
      setErro(null);
      void qc.invalidateQueries({ queryKey: chave });
    },
    onError: (e) => setErro(extractError(e).message),
  });

  if (q.isLoading) return <PageLayout title="Conselheiro">Carregando…</PageLayout>;
  if (q.isError || !q.data) {
    return (
      <PageLayout title="Conselheiro">
        <p className="text-sm text-red-600">{extractError(q.error).message || 'Você não atende esta empresa.'}</p>
      </PageLayout>
    );
  }

  const d = q.data;
  const principal = d.meu_papel === 'PRINCIPAL';
  const doCiclo = d.radares.filter((r) => r.ciclo_id && r.ciclo_id === d.ciclo_ativo?.id);
  const aberto = doCiclo.find((r) => r.aberto);
  const fechados = d.radares.filter((r) => !r.aberto);
  const podeAbrir = TIPOS_DE_RADAR.filter(
    (t) =>
      motivoParaRecusarRadar(
        t,
        doCiclo.map((r) => ({ tipo: r.tipo, fechado: !r.aberto })),
        !!d.ciclo_ativo,
      ) === null,
  );

  return (
    <PageLayout
      title={d.empresa.nome}
      description={
        d.ciclo_ativo
          ? `Ciclo: mês ${d.ciclo_ativo.mes} de 12, de ${data(d.ciclo_ativo.inicio_em)} a ${data(d.ciclo_ativo.fim_em)}`
          : 'Sem ciclo ativo — o ciclo é aberto no HAOps.'
      }
      actions={
        <div className="flex gap-4">
          <Link to="/ajuda/conselheiro-para-conselheiros" className="text-xs text-accent underline">
            Como funciona
          </Link>
          <Link to="/direcao/conselheiro" className="text-xs text-accent underline">
            ← Conselheiro
          </Link>
        </div>
      }
    >
      {erro ? <p className="mb-4 text-sm text-red-600">{erro}</p> : null}
      <div className="flex flex-col gap-6">
        <BalancoDoCiclo empresaId={d.empresa.id} empresa={d.empresa.nome} />
        {aberto ? (
          <RadarAberto radar={aberto} principal={principal} chave={chave} empresaId={d.empresa.id} />
        ) : principal && podeAbrir.length > 0 ? (
          <Cartao
            titulo="Abrir radar"
            sub="Cada sócio responde sozinho as 50 perguntas do diagnóstico 5 Forças, aqui no app. Você vê as respostas chegando e fecha o radar com a nota de cada tema."
          >
            <div className="flex flex-wrap gap-2">
              {podeAbrir.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={botao}
                  style={{ background: 'var(--primary)' }}
                  disabled={abrir.isPending}
                  onClick={() => abrir.mutate(t)}
                >
                  Abrir {NOME[t].toLowerCase()}
                </button>
              ))}
            </div>
          </Cartao>
        ) : !principal && !aberto ? (
          <p className="text-sm text-ha-subtle">Nenhum radar aberto. O Conselheiro Principal abre e fecha o radar.</p>
        ) : null}

        {fechados.length > 0 ? <RadaresFechados radares={fechados} /> : null}

        <Cartao
          titulo="O mês"
          sub={principal ? 'Agende a manhã, acompanhe o preenchimento da empresa, monte a pauta e registre as decisões.' : 'A próxima manhã, a pauta e as decisões.'}
        >
          <CicloMensal empresaId={d.empresa.id} principal={principal} />
        </Cartao>

        <Cartao
          titulo="Plano do ciclo"
          sub={principal ? 'Defina na S0, com os sócios: as 4 Metas e de três a cinco indicadores mensais.' : 'Definido pelo Conselheiro Principal.'}
        >
          <PlanoDoCiclo empresaId={d.empresa.id} podeDefinir={principal} />
        </Cartao>

        <Cartao titulo="Concorrentes" sub="Informados pelos sócios.">
          <Concorrentes empresaId={d.empresa.id} />
        </Cartao>
      </div>
    </PageLayout>
  );
}

function RadarAberto({ radar, principal, chave, empresaId }: { radar: Radar; principal: boolean; chave: unknown[]; empresaId: string }) {
  const qc = useQueryClient();
  const plano = usePlano(empresaId);
  const indicadores = (plano.data?.indicadores ?? []).filter((i) => i.ativo);
  const [indicadorDoTema, setIndicadorDoTema] = useState<string[]>(() => TEMAS.map(() => ''));
  const concluidos = radar.socios.filter((s) => s.temas);
  const { media, amplitude, divergentes } = radar.consolidado;

  const series: SerieDoRadar[] = useMemo(
    () => [
      ...concluidos.map((s, i) => ({ rotulo: s.user.name, notas: s.temas!, cor: CORES_DO_RADAR[i % CORES_DO_RADAR.length]! })),
      ...(concluidos.length > 1 ? [{ rotulo: 'Média dos sócios', notas: media, cor: 'var(--primary)', preenchida: true }] : []),
    ],
    [concluidos, media],
  );

  // A nota final começa na média dos sócios; o conselheiro ajusta pela evidência.
  const [notas, setNotas] = useState<string[]>(() => TEMAS.map(() => ''));
  const [evidencias, setEvidencias] = useState<string[]>(() => TEMAS.map(() => ''));
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    setNotas((atual) => (atual.every((x) => x === '') && concluidos.length > 0 ? media.map((m) => String(m)) : atual));
  }, [media, concluidos.length]);

  const fechar = useMutation({
    mutationFn: async () => {
      const valores = notas.map((n) => Number(n.replace(',', '.')));
      if (valores.some((v) => !Number.isFinite(v) || v < 0 || v > 10)) {
        throw new Error('Cada tema precisa de uma nota de 0 a 10.');
      }
      return (
        await api.post(`/conselheiro/radares/${radar.id}/fechar`, {
          notas: valores,
          evidencias: evidencias
            .map((descricao, tema) => {
              const ind = indicadores.find((i) => i.id === indicadorDoTema[tema]);
              // Indicador sem texto vira evidência com o nome dele.
              const texto = descricao.trim() || (ind ? ind.nome : '');
              return { tema, descricao: texto, indicador_id: ind?.id ?? null };
            })
            .filter((e) => e.descricao.length >= 2),
          observacoes: observacoes.trim() || null,
        })
      ).data;
    },
    onSuccess: (dados) => {
      qc.setQueryData(chave, dados);
    },
    onError: (e) => setErro(e instanceof Error && !('response' in e) ? e.message : extractError(e).message),
  });

  const faltam = radar.socios.filter((s) => !s.concluida);

  return (
    <Cartao
      titulo={`${NOME[radar.tipo]} — aberto`}
      sub={`Aberto em ${data(radar.data)}. ${concluidos.length} de ${radar.socios.length} sócio(s) concluíram.`}
    >
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div>
          {concluidos.length === 0 ? (
            <p className="text-sm text-ha-subtle">Nenhum sócio concluiu ainda. O radar aparece conforme as respostas chegam.</p>
          ) : (
            <Radar10 series={series} destaque={divergentes} />
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">Sócios</h3>
            {radar.socios.length === 0 ? (
              <p className="text-sm text-ha-subtle">A empresa não tem sócios cadastrados. Cadastre no HAOps.</p>
            ) : (
              <ul className="flex flex-col gap-1.5 text-sm">
                {radar.socios.map((s) => (
                  <li key={s.user.id} className="flex justify-between gap-2">
                    <span className="text-ha-text">{s.user.name}</span>
                    <span className={s.concluida ? 'text-xs font-semibold text-emerald-600' : 'text-xs text-ha-muted'}>
                      {s.concluida ? 'concluiu' : s.respondidas > 0 ? `${s.respondidas} de 50` : 'não começou'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {divergentes.length > 0 ? (
            <div>
              <h3 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">
                Onde os sócios divergem
              </h3>
              <ul className="flex flex-col gap-1 text-sm">
                {divergentes.map((t) => (
                  <li key={t}>
                    <span className="font-medium text-amber-600">{TEMAS[t]}</span>{' '}
                    <span className="text-xs text-ha-muted">
                      notas de {nota(Math.min(...concluidos.map((s) => s.temas![t]!)))} a{' '}
                      {nota(Math.max(...concluidos.map((s) => s.temas![t]!)))} (diferença de {nota(amplitude[t]!)})
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-ha-muted">
                Diferença de {LIMIAR_DE_DIVERGENCIA} pontos ou mais. Boa pauta para a S0.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {principal ? (
        <div className="mt-6 border-t border-ha-border2 pt-5">
          <h3 className="text-sm font-semibold text-ha-text">Fechar o radar</h3>
          <p className="mb-3 text-xs text-ha-muted">
            A nota começa na média dos sócios. Ajuste pelo que você viu e registre a evidência que sustenta cada nota — um
            indicador, um fato. Fechado, o autodiagnóstico para de receber resposta.
            {faltam.length > 0 ? ` Ainda falta: ${faltam.map((s) => s.user.name).join(', ')}.` : ''}
          </p>
          <div className="grid gap-2">
            <div className="hidden gap-2 text-[11px] font-medium text-ha-muted sm:grid sm:grid-cols-[130px_80px_1fr_200px]">
              <span>Tema</span>
              <span className="flex items-center gap-1">
                Nota <Dica texto="De 0 a 10. Começa na média dos sócios; ajuste pelo que você viu." />
              </span>
              <span className="flex items-center gap-1">
                Evidência{' '}
                <Dica texto="O fato que sustenta a nota: o que existe, o que acontece, um número. Opcional, mas é o que torna a nota defensável." />
              </span>
              <span className="flex items-center gap-1">
                Indicador{' '}
                <Dica texto="Um indicador da empresa que comprova o tema. Os do mesmo tema aparecem primeiro, com ★." />
              </span>
            </div>
            {TEMAS.map((t, i) => (
              <div key={t} className="grid items-start gap-2 sm:grid-cols-[130px_80px_1fr_200px]">
                <label htmlFor={`nota-${i}`} className="pt-2 text-sm text-ha-text">
                  {t}
                </label>
                <input
                  id={`nota-${i}`}
                  inputMode="decimal"
                  className="rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm"
                  value={notas[i]}
                  onChange={(e) => setNotas((n) => n.map((x, k) => (k === i ? e.target.value : x)))}
                />
                <input
                  aria-label={`Evidência de ${t}`}
                  placeholder="Evidência (opcional): indicador ou fato"
                  className="rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm"
                  value={evidencias[i]}
                  onChange={(e) => setEvidencias((v) => v.map((x, k) => (k === i ? e.target.value : x)))}
                />
                <select
                  aria-label={`Indicador que comprova ${t}`}
                  className="rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm"
                  value={indicadorDoTema[i]}
                  onChange={(e) => setIndicadorDoTema((v) => v.map((x, k) => (k === i ? e.target.value : x)))}
                >
                  <option value="">Indicador (opcional)</option>
                  {[...indicadores]
                    .sort((a, b) => Number(b.tema === i) - Number(a.tema === i))
                    .map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.nome}
                        {ind.tema === i ? ' ★' : ''}
                      </option>
                    ))}
                </select>
              </div>
            ))}
          </div>
          <textarea
            className="mt-3 w-full rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm"
            rows={3}
            placeholder="Observações (opcional)"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
          />
          {erro ? <p className="mt-2 text-sm text-red-600">{erro}</p> : null}
          <button
            type="button"
            className={`${botao} mt-3`}
            style={{ background: 'var(--primary)' }}
            disabled={fechar.isPending}
            onClick={() => {
              setErro(null);
              if (window.confirm('Fechar o radar com estas notas? Depois de fechado, não muda.')) fechar.mutate();
            }}
          >
            Fechar radar
          </button>
        </div>
      ) : null}
    </Cartao>
  );
}

const TRACOS: Record<Tipo, Partial<SerieDoRadar>> = {
  PARTIDA: { tracejada: true, cor: '#94a3b8' },
  MARCO: { cor: '#60a5fa' },
  FINAL: { cor: 'var(--primary)', preenchida: true },
};

function RadaresFechados({ radares }: { radares: Radar[] }) {
  const comNotas = radares.filter((r) => r.notas);
  const series: SerieDoRadar[] = comNotas.map((r, i) => ({
    rotulo: `${NOME[r.tipo]} · ${data(r.fechado_em)}`,
    notas: r.notas!,
    cor: CORES_DO_RADAR[i % CORES_DO_RADAR.length]!,
    ...TRACOS[r.tipo],
  }));
  const ultimo = comNotas[comNotas.length - 1];
  return (
    <Cartao titulo="Radares fechados" sub="As notas finais do conselheiro, com a evidência de cada tema.">
      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <Radar10 series={series} />
        {ultimo ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-ha-muted">
                <tr>
                  <th className="py-1 pr-2 font-medium">Tema</th>
                  {comNotas.map((r) => (
                    <th key={r.id} className="py-1 pr-2 font-medium">
                      {NOME[r.tipo].replace('Radar ', '')}
                    </th>
                  ))}
                  <th className="py-1 font-medium">Evidência ({NOME[ultimo.tipo].replace('Radar ', '')})</th>
                </tr>
              </thead>
              <tbody>
                {TEMAS.map((t, i) => (
                  <tr key={t} className="border-t border-ha-border2">
                    <td className="py-1.5 pr-2 text-ha-text">{t}</td>
                    {comNotas.map((r) => (
                      <td key={r.id} className="py-1.5 pr-2 font-semibold">
                        {nota(r.notas![i]!)}
                      </td>
                    ))}
                    <td className="py-1.5 text-xs text-ha-muted">
                      {ultimo.evidencias
                        .filter((e) => e.tema === i)
                        .map((e) => (e.indicador && e.indicador.nome !== e.descricao ? `${e.descricao} (${e.indicador.nome})` : e.descricao))
                        .join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {ultimo.observacoes ? <p className="mt-3 text-xs text-ha-muted">{ultimo.observacoes}</p> : null}
          </div>
        ) : null}
      </div>
    </Cartao>
  );
}
